<template>
  <div ref="root" class="world-map">
    <div class="map-toolbar">
      <ul class="map-legend">
        <li v-for="item in legend" :key="item.state">
          <svg
            class="legend-icon"
            :viewBox="`0 0 ${item.sprite.w} ${item.sprite.h}`"
            :width="item.sprite.w * 1.5"
            :height="item.sprite.h * 1.5"
            shape-rendering="crispEdges"
            aria-hidden="true"
          >
            <path
              v-for="layer in item.sprite.layers"
              :key="layer.fill"
              :d="layer.d"
              :fill="layer.fill"
            />
          </svg>
          {{ item.label }}
        </li>
      </ul>
      <button
        type="button"
        class="button debug-toggle"
        :class="{ current: debug }"
        :aria-pressed="debug"
        title="Debug screen: chunk borders and block info (F3)"
        @click="debug = !debug"
      >
        F3
      </button>
    </div>
    <div class="map-frame">
      <div
        v-if="layout"
        ref="viewport"
        class="map-viewport"
        :style="viewportStyle"
        @pointermove="onPointerMove"
        @pointerleave="cursor = null"
      >
        <canvas
          ref="base"
          class="map-layer"
          :width="layout.w"
          :height="layout.h"
          aria-hidden="true"
        />
        <canvas
          ref="fx"
          class="map-layer"
          :width="layout.w"
          :height="layout.h"
          aria-hidden="true"
        />
        <div
          v-if="debug"
          class="chunk-grid"
          aria-hidden="true"
        />

        <div class="debug-hud" aria-hidden="true">
          <span>challgame (vanilla)</span>
          <span>C: {{ solvedCount }}/{{ markers.length }} solved</span>
          <template v-if="cursor">
            <span>XYZ: {{ cursor.x }} / {{ cursor.y }} / {{ cursor.z }}</span>
            <span>Biome: {{ cursor.biome }}</span>
            <template v-if="debug">
              <span>Chunk: {{ cursor.bx >> 4 }} {{ cursor.by >> 4 }} in r.0.0.mca</span>
              <span v-if="cursor.region">Category: {{ cursor.region }}</span>
            </template>
          </template>
        </div>

        <span
          v-for="region in regionLabels"
          :key="region.name"
          class="region-label"
          :style="region.style"
        >
          {{ region.name }}
          <span class="region-count">{{ region.solved }}/{{ region.total }}</span>
        </span>

        <button
          v-for="(marker, index) in markers"
          :key="marker.chall.id"
          type="button"
          class="map-marker"
          :class="[marker.state, { active: index === active }]"
          :style="marker.style"
          :aria-label="marker.ariaLabel"
          :aria-disabled="!marker.openable"
          @click="open(marker)"
          @pointerenter="active = index"
          @pointerleave="active = -1"
          @focus="active = index"
          @blur="active = -1"
        >
          <span v-if="marker.unsolvedByAll" class="portal-aura">
            <PortalParticles spawn="edge" />
          </span>
          <span class="marker-icon-box">
            <svg
              class="marker-icon"
              :viewBox="`0 0 ${marker.sprite[0].w} ${marker.sprite[0].h}`"
              :width="marker.sprite[0].w * 2"
              :height="marker.sprite[0].h * 2"
              shape-rendering="crispEdges"
              aria-hidden="true"
            >
              <g
                v-for="(frame, f) in marker.sprite"
                :key="f"
                :class="`frame-${f}`"
              >
                <path
                  v-for="layer in frame.layers"
                  :key="layer.fill"
                  :d="layer.d"
                  :fill="layer.fill"
                />
              </g>
            </svg>
          </span>
          <span class="marker-name" aria-hidden="true">
            <span class="marker-title">{{ marker.title || obfuscate(marker, tick) }}</span>
            <span class="marker-score">{{ marker.score }}</span>
          </span>
        </button>

        <div
          v-if="activeMarker"
          class="map-tooltip"
          :class="tooltipClass"
          :style="tooltipStyle"
          aria-hidden="true"
        >
          <span class="tt-title" :class="activeMarker.rarity">{{ activeMarker.title || obfuscate(activeMarker, tick) }}</span>
          <span class="tt-gray">{{ activeMarker.chall.categories.join(', ') }}</span>
          <span v-if="activeMarker.authors" class="tt-lore">by {{ activeMarker.authors }}</span>
          <span class="tt-blank" />
          <span class="tt-gray">When solved:</span>
          <span class="tt-blue">+{{ activeMarker.score }} Points</span>
          <span class="tt-blue">{{ activeMarker.chall.solves }} {{ activeMarker.chall.solves === 1 ? 'Solve' : 'Solves' }}</span>
          <span v-if="activeMarker.state === 'solved'" class="tt-green">✔ Solved by your team</span>
          <span v-else-if="activeMarker.state === 'locked'" class="tt-red">Solve the previous challenge to unlock it.</span>
          <span v-else-if="activeMarker.state === 'unreleased'" class="tt-red">Not published yet. It will appear during the competition.</span>
          <span class="tt-dark">ssm:{{ activeMarker.region }}/{{ activeMarker.chall.id }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PortalParticles from '@/components/PortalParticles.vue'
import { reducedMotion, watchReducedMotion } from '@/motion.js'
import { toCanvas } from '@/pixel/raster.js'
import { SPRITES } from '@/pixel/sprites.js'
import { CHUNK, describeBlock, generateWorld } from '@/pixel/world.js'

// The challenges as a Minecraft world: every category is a biome, every
// challenge a flag planted in it, and the links of a chain are joined by
// redstone dust that powers up as they are solved. The world is the same
// for everyone (it only depends on the categories and the map size), loads
// chunk by chunk like the game does and glitches every now and then.

// Minecraft's §k: random characters of the same width, for unreleased challenges.
const OBFUSCATED = 'ABCDEFGHJKLMNOPQRSTUVWXYZabdefghkmnopqrstuwxyz0123456789#$%&?@'
// Height of the icon box; the bottom of every icon sits on its block.
const ICON_BOX = 26
// The loading screen's chunk colours, a step at a time, darkened.
const LOADING = ['#1b1530', '#2e2747', '#47406a']

function solvedBy (team, chall) {
    return !!team && chall.id in team.solves
}

export default {
    components: {
        PortalParticles,
    },
    data () {
        return {
            layout: null,
            cursor: null,
            active: -1,
            debug: false,
            tick: 0,
            reduced: reducedMotion(),
        }
    },
    computed: {
        // Each category's challenges in map order: by order, the links of a
        // chain after its first challenge, as in the list view.
        groups () {
            const all = this.$store.state.challenges
            return this.$store.state.categories.map(name => {
                const heads = Object.values(all).filter(c => c.categories[0] === name && c.unlocked_by === null).sort((a, b) => a.order - b.order)
                const list = []
                for (const head of heads) {
                    let c = head
                    list.push(c)
                    while (c.unlocks && all[c.unlocks] && !list.includes(all[c.unlocks])) {
                        c = all[c.unlocks]
                        list.push(c)
                    }
                }
                return { name, list }
            }).filter(g => g.list.length)
        },
        layoutKey () {
            return this.groups.map(g => `${g.name}:${g.list.length}`).join('|')
        },
        rarity () {
            // Minecraft's rarity colours, by how much a challenge is worth
            // compared to the rest: the rarest solves are epic.
            const scores = Object.values(this.$store.state.challenges)
                .filter(c => c.status !== 2)
                .map(c => this.$store.getters.challScore(c.id))
                .sort((a, b) => b - a)
            return score => {
                const rank = scores.indexOf(score) / Math.max(1, scores.length)
                return rank < 0.15 ? 'epic' : rank < 0.4 ? 'rare' : rank < 0.7 ? 'uncommon' : 'common'
            }
        },
        markers () {
            const layout = this.layout
            if (!layout || layout.groupKey !== this.layoutKey) return []
            const team = this.$store.state.team
            const cols = Math.ceil(layout.w / CHUNK)
            const rows = Math.ceil(layout.h / CHUNK)
            const out = []
            this.groups.forEach((group, r) => {
                group.list.forEach((chall, k) => {
                    const slot = layout.regions[r] && layout.regions[r].slots[k]
                    if (!slot) return
                    let state = 'open'
                    if (chall.status === 2) state = 'unreleased'
                    else if (chall.status === 1) state = 'locked'
                    else if (solvedBy(team, chall)) state = 'solved'
                    const x = (slot.x + 0.5) * layout.px
                    const y = (slot.y + 0.5) * layout.px
                    const score = this.$store.getters.challScore(chall.id)
                    const title = chall.status === 2 ? null : chall.title
                    const note = { solved: ', solved', locked: ', locked, solve the previous challenge to unlock it', unreleased: ', not published yet' }[state] || ''
                    // Planted once its chunk has loaded, see reveal().
                    const chunkDistance = Math.hypot((slot.x >> 4) - (cols - 1) / 2, (slot.y >> 4) - (rows - 1) / 2) / (Math.hypot((cols - 1) / 2, (rows - 1) / 2) || 1)
                    out.push({
                        chall,
                        state,
                        x,
                        y,
                        slot,
                        title,
                        score,
                        region: group.name,
                        rarity: this.rarity(score),
                        authors: (chall.authors || []).join(', '),
                        openable: state === 'open' || state === 'solved',
                        // Nobody has solved it yet: portal particles around the flag.
                        unsolvedByAll: state !== 'unreleased' && chall.solves === 0,
                        sprite: SPRITES[state],
                        style: {
                            left: `${x}px`,
                            top: `${y - ICON_BOX}px`,
                            '--delay': `${Math.round(chunkDistance * 650 + 260)}ms`,
                            '--phase': `${-((chall.id * 377) % 1200)}ms`,
                        },
                        ariaLabel: `${title || 'Unreleased challenge'}, ${group.name}, ${score} points, ${chall.solves} solves${note}`,
                    })
                })
            })
            return out
        },
        solvedCount () {
            return this.markers.filter(m => m.state === 'solved').length
        },
        regionLabels () {
            if (!this.layout) return []
            const { px, regions } = this.layout
            return regions.map(region => {
                const own = this.markers.filter(m => m.region === region.name)
                return {
                    name: region.name,
                    total: own.length,
                    solved: own.filter(m => m.state === 'solved').length,
                    style: { left: `${(region.label.x + 0.5) * px}px`, top: `${(region.label.y + 0.5) * px}px` },
                }
            })
        },
        // Redstone dust between the links of a chain, powered once the link
        // before it is solved.
        paths () {
            const byId = {}
            for (const m of this.markers) byId[m.chall.id] = m
            const out = []
            for (const m of this.markers) {
                const from = byId[m.chall.unlocked_by]
                if (!from) continue
                const blocks = []
                const { x: x0, y: y0 } = from.slot
                const { x: x1, y: y1 } = m.slot
                for (let x = x0; x !== x1; x += Math.sign(x1 - x0)) blocks.push([x, y0])
                for (let y = y0; y !== y1; y += Math.sign(y1 - y0)) blocks.push([x1, y])
                blocks.push([x1, y1])
                out.push({ blocks, powered: from.state === 'solved' })
            }
            return out
        },
        pathKey () {
            return this.paths.map(p => `${p.blocks[0]}>${p.blocks[p.blocks.length - 1]}:${p.powered}`).join('|')
        },
        hasUnreleased () {
            return this.markers.some(m => m.state === 'unreleased')
        },
        activeMarker () {
            return this.markers[this.active] || null
        },
        viewportStyle () {
            const { w, h, px } = this.layout
            return { width: `${w * px}px`, height: `${h * px}px`, '--block': `${px}px` }
        },
        tooltipStyle () {
            const m = this.activeMarker
            return { left: `${m.x}px`, top: `${m.y}px` }
        },
        tooltipClass () {
            const m = this.activeMarker
            const width = this.layout.w * this.layout.px
            return { below: m.y < 200, 'align-left': m.x < 150, 'align-right': m.x > width - 150 }
        },
        legend () {
            const labels = { open: 'Open', solved: 'Solved', locked: 'Locked', unreleased: 'Unreleased' }
            return Object.keys(labels).map(state => ({ state, label: labels[state], sprite: SPRITES[state][0] }))
        },
    },
    watch: {
        // Before the render, so the markers never show against the old map.
        layoutKey () {
            this.build()
        },
        pathKey () {
            this.compose()
            if (!this.revealing) this.present()
        },
        solvedCount (now, before) {
            if (now > before) this.burst(2.5)
        },
        hasUnreleased () {
            this.syncTicker()
        },
        reduced () {
            this.syncTicker()
            this.scheduleBurst()
        },
    },
    mounted () {
        this.revealed = false
        this.build()
        this.observer = new ResizeObserver(() => {
            clearTimeout(this.resizeTimer)
            this.resizeTimer = setTimeout(this.build, 200)
        })
        this.observer.observe(this.$refs.root)
        this.unwatchMotion = watchReducedMotion(() => (this.reduced = reducedMotion()))
        this.onKey = e => {
            if (e.key !== 'F3') return
            e.preventDefault()
            this.debug = !this.debug
        }
        window.addEventListener('keydown', this.onKey)
        this.syncTicker()
        this.scheduleBurst()
    },
    beforeUnmount () {
        this.observer.disconnect()
        this.unwatchMotion()
        window.removeEventListener('keydown', this.onKey)
        cancelAnimationFrame(this.revealFrame)
        clearTimeout(this.resizeTimer)
        clearTimeout(this.burstTimer)
        clearTimeout(this.frameTimer)
        clearInterval(this.ticker)
    },
    methods: {
        build () {
            const root = this.$refs.root
            if (!root || !this.groups.length) {
                this.layout = null
                return
            }
            const avail = root.clientWidth - 8
            const px = avail >= 720 ? 6 : 5
            const w = Math.max(48, Math.floor(avail / px))
            // A marker is its 26 px icon over a name up to 8rem (144 px)
            // wide. Each gets about one and a half times that in land, plus
            // two more per category for its name, on a map that's roughly two
            // thirds land.
            const marker = { w: 150 / px, h: 50 / px }
            const cell = 1.5 * marker.w * marker.h
            const total = this.groups.reduce((sum, g) => sum + g.list.length + 2, 0)
            const wide = avail >= 720
            const h = Math.round(Math.min(w * 6, Math.max(w * (wide ? 0.5 : 1.1), (total * cell) / 0.62 / w)))
            const key = `${this.layoutKey}@${w}x${h}`
            if (this.layout && this.layout.key === key) return

            // Region names are set in 13.5 px Monocraft, two thirds of an em a character.
            const labelPx = name => (name.length + 6) * 9 + 24
            this.world = generateWorld({
                w,
                h,
                marker,
                regions: this.groups.map(g => ({
                    name: g.name,
                    count: g.list.length,
                    label: { w: Math.ceil(labelPx(g.name) / px), h: Math.ceil(30 / px) },
                })),
            })
            this.terrain = toCanvas(this.world.image)
            this.layout = {
                key,
                groupKey: this.layoutKey,
                w,
                h,
                px,
                regions: this.world.regions.map(r => ({ name: r.name, label: r.label, slots: r.slots })),
            }
            this.cursor = null
            this.active = -1
            this.$nextTick(() => {
                this.compose()
                if (this.revealed) this.present()
                else this.reveal()
            })
        },
        // Terrain and redstone on an offscreen canvas, for the layers to copy from.
        compose () {
            if (!this.world) return
            const { w, h } = this.world
            if (!this.composed) this.composed = document.createElement('canvas')
            this.composed.width = w
            this.composed.height = h
            const g = this.composed.getContext('2d')
            g.drawImage(this.terrain, 0, 0)
            for (const path of this.paths) {
                // RedStoneWireBlock's colours at power 15 and 0, speckled like the dust.
                const colors = path.powered ? ['#ff3300', '#c81e00'] : ['#7a0b0b', '#4c0000']
                for (const [x, y] of path.blocks) {
                    g.fillStyle = colors[(x + y) & 1]
                    g.fillRect(x, y, 1, 1)
                }
            }
        },
        present () {
            const base = this.$refs.base
            if (!base || !this.composed) return
            const ctx = base.getContext('2d')
            ctx.clearRect(0, 0, base.width, base.height)
            ctx.drawImage(this.composed, 0, 0)
        },
        // Chunks load outwards from the middle, each going through a few
        // shades of the loading screen before its terrain shows up.
        reveal () {
            this.revealed = true
            if (this.reduced || !this.$refs.base) return this.present()
            const ctx = this.$refs.base.getContext('2d')
            const { w, h } = this.world
            const cols = Math.ceil(w / CHUNK)
            const rows = Math.ceil(h / CHUNK)
            const far = Math.hypot((cols - 1) / 2, (rows - 1) / 2) || 1
            const chunks = []
            for (let cy = 0; cy < rows; cy++) {
                for (let cx = 0; cx < cols; cx++) {
                    const d = Math.hypot(cx - (cols - 1) / 2, cy - (rows - 1) / 2) / far
                    chunks.push({ x: cx * CHUNK, y: cy * CHUNK, t: d * 650 + Math.random() * 140, done: false })
                }
            }
            ctx.clearRect(0, 0, w, h)
            this.revealing = true
            const start = performance.now()
            const step = now => {
                let left = 0
                for (const c of chunks) {
                    if (c.done) continue
                    left++
                    const phase = now - start - c.t
                    if (phase < 0) continue
                    if (phase < 120) {
                        ctx.fillStyle = LOADING[Math.min(LOADING.length - 1, (phase / 40) | 0)]
                        ctx.fillRect(c.x, c.y, CHUNK, CHUNK)
                    } else {
                        ctx.clearRect(c.x, c.y, CHUNK, CHUNK)
                        ctx.drawImage(this.composed, c.x, c.y, CHUNK, CHUNK, c.x, c.y, CHUNK, CHUNK)
                        c.done = true
                    }
                }
                if (left) {
                    this.revealFrame = requestAnimationFrame(step)
                } else {
                    this.revealing = false
                    this.present()
                }
            }
            this.revealFrame = requestAnimationFrame(step)
        },
        scheduleBurst () {
            clearTimeout(this.burstTimer)
            if (this.reduced) return
            this.burstTimer = setTimeout(() => this.burst(), 5000 + Math.random() * 8000)
        },
        // A few rows of the map tear sideways for a handful of frames. Big
        // bursts (someone solved something) also flash chunks as missing.
        burst (strength = 1) {
            const fx = this.$refs.fx
            if (this.reduced || !fx || !this.composed || document.hidden || this.revealing) return this.scheduleBurst()
            clearTimeout(this.burstTimer)
            clearTimeout(this.frameTimer)
            const ctx = fx.getContext('2d')
            const { w, h } = this.world
            let frames = Math.round((2 + Math.random() * 3) * strength)
            const step = () => {
                ctx.clearRect(0, 0, w, h)
                if (frames-- <= 0) return this.scheduleBurst()
                for (let i = Math.round((1 + Math.random() * 2) * strength); i > 0; i--) {
                    const y = Math.floor(Math.random() * h)
                    const rows = 1 + Math.floor(Math.random() * 4 * strength)
                    const dx = Math.round((Math.random() - 0.5) * 10 * strength)
                    ctx.drawImage(this.composed, 0, y, w, rows, dx, y, w, rows)
                }
                if (strength > 1 && Math.random() < 0.6) {
                    const x0 = Math.floor(Math.random() * Math.ceil(w / CHUNK)) * CHUNK
                    const y0 = Math.floor(Math.random() * Math.ceil(h / CHUNK)) * CHUNK
                    for (let y = 0; y < CHUNK; y++) {
                        for (let x = 0; x < CHUNK; x++) {
                            ctx.fillStyle = ((x >> 1) + (y >> 1)) & 1 ? '#000000' : '#f800f8'
                            ctx.fillRect(x0 + x, y0 + y, 1, 1)
                        }
                    }
                }
                this.frameTimer = setTimeout(step, 50 + Math.random() * 60)
            }
            step()
        },
        syncTicker () {
            clearInterval(this.ticker)
            if (this.hasUnreleased && !this.reduced) this.ticker = setInterval(() => this.tick++, 90)
        },
        obfuscate (marker, tick) {
            const length = 5 + (marker.chall.id % 5)
            let out = ''
            for (let i = 0; i < length; i++) {
                const n = Math.imul(marker.chall.id * 31 + i * 7919 + tick * 104729, 2654435761) >>> 0
                out += this.reduced ? '?' : OBFUSCATED[n % OBFUSCATED.length]
            }
            return out
        },
        onPointerMove (e) {
            if (!this.world || !this.$refs.viewport) return
            const rect = this.$refs.viewport.getBoundingClientRect()
            const x = Math.floor((e.clientX - rect.left) / this.layout.px)
            const y = Math.floor((e.clientY - rect.top) / this.layout.px)
            if (x < 0 || y < 0 || x >= this.world.w || y >= this.world.h) return
            if (this.cursor && this.cursor.bx === x && this.cursor.by === y) return
            this.cursor = { ...describeBlock(this.world, x, y), bx: x, by: y }
        },
        open (marker) {
            if (!marker.openable) return
            this.$router.push({ name: 'challenge', params: { id: marker.chall.id } })
        },
    },
}
</script>

<style lang="scss" scoped>
.world-map {
    position: relative;
}

.map-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin-bottom: 0.75rem;
}

.map-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1.25rem;
    margin: 0;
    padding: 0;
    list-style: none;
    color: $ssm-muted;

    li {
        display: flex;
        align-items: center;
        gap: 0.4rem;
    }
}

.legend-icon {
    display: block;
}

.debug-toggle {
    padding: 0.1rem 0.6rem;

    &.current {
        border-color: #ffff55;
        color: #ffff55;
    }
}

.map-frame {
    @include mc-tooltip;
    display: flex;
    justify-content: center;
    overflow: hidden;
}

.map-viewport {
    position: relative;
    flex: none;
    cursor: crosshair;
}

.map-layer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    image-rendering: pixelated;
}

// F3 + G: chunk borders.
.chunk-grid {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
        linear-gradient(to right, rgba(255, 255, 85, 0.45) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 85, 0.45) 1px, transparent 1px);
    background-size: calc(var(--block) * 16) calc(var(--block) * 16);
}

// The debug screen's grey boxes behind each line.
.debug-hud {
    position: absolute;
    top: 0.375rem;
    left: 0.375rem;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    font-size: 0.75rem;
    line-height: 1.35;
    color: #e0e0e0;
    pointer-events: none;

    span {
        padding: 0 0.25rem;
        background: rgba(80, 80, 80, 0.56);
    }
}

.region-label {
    @include mc-tooltip(0.86);
    position: absolute;
    z-index: 1;
    transform: translate(-50%, -50%);
    padding: 0 0.5rem;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.6;
    text-transform: uppercase;
    white-space: nowrap;
    color: $ssm-teal;
    pointer-events: none;
}

.region-count {
    font-weight: 400;
    color: $ssm-muted;
}

.map-marker {
    position: absolute;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
    transform: translateX(-50%);
    animation: marker-plant 0.36s steps(4, end) var(--delay) both;
    -webkit-tap-highlight-color: transparent;

    &.active {
        z-index: 4;
    }

    &:focus-visible {
        outline: none;

        .marker-name {
            outline: 2px dashed $ssm-teal;
            outline-offset: 1px;
        }
    }
}

.marker-icon-box {
    display: flex;
    align-items: flex-end;
    height: 26px;
}

// Around the flag and its name, a little tighter than on the list cards.
.map-marker > .portal-aura {
    inset: -0.75rem -1rem;
}

.marker-icon {
    display: block;
    filter: drop-shadow(2px 2px 0 rgba(0, 0, 0, 0.45));
}

.active .marker-icon {
    transform: translateY(-2px);
}

// The flags wave: two frames, swapped every half second.
.frame-1 {
    visibility: hidden;
}

.open {
    .frame-0 {
        animation: wave-a 1s steps(1, end) var(--phase) infinite;
    }

    .frame-1 {
        animation: wave-b 1s steps(1, end) var(--phase) infinite;
    }
}

.marker-name {
    display: flex;
    gap: 0.35em;
    max-width: 8rem;
    margin-top: 1px;
    padding: 0 0.25rem;
    font-size: 0.75rem;
    line-height: 1.4;
    white-space: nowrap;
    background: rgba($ssm-ink, 0.82);
    border: 1px solid transparent;
}

.active .marker-name {
    border-color: #ffffff;
}

.marker-title {
    overflow: hidden;
    text-overflow: ellipsis;
}

.marker-score {
    flex: none;
    color: $ssm-gold;
}

.solved .marker-title {
    color: $ssm-green;
}

.locked,
.unreleased {
    cursor: default;

    .marker-name {
        color: #8a8199;
    }

    .marker-score {
        color: #8a8199;
    }
}

.unreleased .marker-title {
    color: #c86ad0;
}

// A Minecraft item tooltip.
.map-tooltip {
    @include mc-tooltip(0.96);
    --tx: -50%;
    --ty: calc(-100% - 26px - 0.75rem); // above the icon box
    position: absolute;
    z-index: 5;
    display: flex;
    flex-direction: column;
    min-width: 13rem;
    max-width: 19rem;
    padding: 0.25rem 0.5rem 0.375rem;
    font-size: 0.875rem;
    line-height: 1.45;
    pointer-events: none;
    transform: translate(var(--tx), var(--ty));

    &.below {
        --ty: 2.25rem;
    }

    &.align-left {
        --tx: -1.25rem;
    }

    &.align-right {
        --tx: calc(-100% + 1.25rem);
    }
}

.tt-title {
    color: #ffffff;

    &.uncommon {
        color: #ffff55;
    }

    &.rare {
        color: #55ffff;
    }

    &.epic {
        color: #ff55ff;
    }
}

.tt-gray {
    color: #aaaaaa;
}

.tt-lore {
    color: #c070e0;
    font-style: italic;
}

.tt-blank {
    height: 0.5rem;
}

.tt-blue {
    color: #8080ff;
}

.tt-green {
    color: #55ff55;
}

.tt-red {
    color: #ff5555;
}

.tt-dark {
    margin-top: 0.25rem;
    color: #75708a;
}

@keyframes marker-plant {
    0% {
        opacity: 0;
        transform: translate(-50%, -18px);
    }
    100% {
        opacity: 1;
        transform: translateX(-50%);
    }
}

@keyframes wave-a {
    0% {
        visibility: visible;
    }
    50% {
        visibility: hidden;
    }
}

@keyframes wave-b {
    0% {
        visibility: hidden;
    }
    50% {
        visibility: visible;
    }
}

@media (prefers-reduced-motion: reduce) {
    .map-marker {
        animation: none;
    }

    .open .frame-0,
    .open .frame-1 {
        animation: none;
    }
}
</style>
